import { pipe } from 'fp-ts/function'
import { fold, fromNullable } from 'fp-ts/Option'

export interface CallbackMessage<TEvent extends string> {
  eventName: TEvent
  id: string
  type: 'dfhCallback'
}

export type OnHubSpotTrackingReadyMessage =
  CallbackMessage<'onHubSpotTrackingReady'>

export interface OnResizeMessage extends CallbackMessage<'onResize'> {
  data: {
    width: number
    height: number
  }
}

export interface OnInstapageNavigateMessage
  extends CallbackMessage<'onInstapageNavigate'> {
  data: {
    href: string
    target?: string
  }
}

const postCallbackMessage = <
  TEvent extends string,
  TMessage extends CallbackMessage<TEvent>
>(
  parentOrigin: string,
  message: TMessage
) => {
  window.parent.postMessage(message, parentOrigin)
}

export const postOnResizeMessage =
  (parentOrigin: string) =>
  ({ id, data }: Pick<OnResizeMessage, 'id' | 'data'>) =>
    postCallbackMessage(parentOrigin, {
      type: 'dfhCallback',
      eventName: 'onResize',
      id,
      data,
    })

export const postOnHubSpotTrackingReadyMessage =
  (parentOrigin: string) =>
  ({ id }: Pick<OnHubSpotTrackingReadyMessage, 'id'>) =>
    postCallbackMessage(parentOrigin, {
      type: 'dfhCallback',
      eventName: 'onHubSpotTrackingReady',
      id,
    })

export const postOnInstapageNavigateMessage =
  (parentOrigin: string) =>
  ({ id, data }: Pick<OnInstapageNavigateMessage, 'id' | 'data'>) =>
    postCallbackMessage(parentOrigin, {
      type: 'dfhCallback',
      eventName: 'onInstapageNavigate',
      id,
      data,
    })

const borderBoxSizeFrom = (
  entry: ResizeObserverEntry | undefined
): { width: number; height: number } | undefined => {
  if (!entry) {
    return undefined
  }
  if (entry.borderBoxSize) {
    const { blockSize: height, inlineSize: width } =
      entry.borderBoxSize[0] ?? {}
    return { width, height }
  }
  if (!entry.contentRect) {
    return undefined
  }
  // Safari only supports `contentRect`
  const { width, height } = entry.contentRect
  return { width, height }
}

export function observeBodyResize({
  id,
  parentOrigin,
}: {
  id: string
  parentOrigin: string
}) {
  const observer = new ResizeObserver(([entry]) => {
    const borderBoxSize = borderBoxSizeFrom(entry)
    if (!borderBoxSize) {
      return
    }
    pipe({ id, data: borderBoxSize }, postOnResizeMessage(parentOrigin))
  })
  pipe(
    fromNullable(document.querySelector('body')),
    fold(
      () => {
        console.error(`[onResizeListener] body element not found`)
      },
      (bodyElement) => observer.observe(bodyElement)
    )
  )
}
